import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const filteredArticles = useMemo(() => {
    let result = [...articles];

    if (activeCategory !== 'all') {
      result = result.filter((a) => a.category === activeCategory);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.code.toLowerCase().includes(q) ||
          a.title.toLowerCase().includes(q) ||
          a.studio.toLowerCase().includes(q) ||
          a.label.toLowerCase().includes(q) ||
          a.actors.some((actor) => actor.toLowerCase().includes(q)) ||
          a.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());
        break;
    }

    return result;
  }, [activeCategory, searchQuery, sortBy]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部番号'} - 番号吧`,
    description: `浏览番号吧${currentCategory?.name || '全部番号'}分类下的番号作品，快速筛选你感兴趣的内容。`,
    url: `https://miaoxiaobao.cn/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '番号吧',
    },
  };

  usePageMeta({
    title: `${currentCategory?.name || '全部番号'} - 番号吧`,
    description: `番号吧${currentCategory?.name || '全部番号'}分类，提供海量番号信息检索和筛选服务。`,
    canonicalUrl: `https://miaoxiaobao.cn/category/${activeCategory}`,
    jsonLd: [webpageSchema],
  });

  return (
    <main>
      <Breadcrumb
        items={[
          { label: currentCategory?.name || '全部番号' },
        ]}
      />

      <section className="w-full px-4 md:px-6 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              {searchQuery ? `搜索：${searchQuery}` : currentCategory?.name || '全部番号'}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共找到 {filteredArticles.length} 个结果
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-1 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1 text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序：</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'latest' | 'rating' | 'views')}
                className="px-3 py-1.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="latest">最新发布</option>
                <option value="rating">评分最高</option>
                <option value="views">热度最高</option>
              </select>
            </div>
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">未找到相关番号信息</p>
              <p className="text-sm text-foreground-400 mt-1">试试其他关键词或分类吧</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}